import { CheckCircleFilled, CloseOutlined, LeftOutlined, PlusOutlined, ReloadOutlined, VideoCameraOutlined } from '@ant-design/icons'
import { Button, DatePicker, Input, Modal, Radio, Select, Skeleton, Spin, Tag, message } from 'antd'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  addPublishPlatform,
  checkPublishConfirmable,
  confirmPublish,
  duplicatePublishItemAsDraft,
  focusPublishWindow,
  getPlatformAccount,
  getPublishItemDetail,
  getPublishJobProgress,
  listLocalPublisherAgentsForSelection,
  listOnboardingDevices,
  listPlatformAccounts,
  listPlatformCapabilities,
  removePublishPlatform,
  retryPlatformAccountOnboarding,
  startPlatformAccountOnboarding,
  updatePublishItem,
  updatePublishPackage,
  videoGenTaskOutputUrl,
  videoGenTaskPosterUrl,
  type LocalPublisherAgentSelectionOut,
  type PlatformAccountOut,
  type PlatformCapabilityOut,
  type PlatformPublishPackageOut,
  type PublishConfirmCheckResult,
  type PublishItemDetail,
  type PublishJobProgressOut,
  type PublishMode,
} from '../api'
import { formatDateTime } from '../../i18n/dateFormat'
import { useUiLocale } from '../../i18n/LocaleProvider'
import './content-publishing.css'
import '../ui/tenant-items.css'

interface PkgDraft {
  title: string
  caption: string
  hashtagsText: string
}

function toDraft(pkg: PlatformPublishPackageOut): PkgDraft {
  return { title: pkg.title, caption: pkg.caption, hashtagsText: pkg.hashtags.map((h) => `#${h}`).join(' ') }
}

function draftDirty(pkg: PlatformPublishPackageOut, draft: PkgDraft): boolean {
  return draft.title !== pkg.title || draft.caption !== pkg.caption || draft.hashtagsText !== pkg.hashtags.map((h) => `#${h}`).join(' ')
}

function parseHashtags(text: string): string[] {
  return text
    .split(/[\s,，]+/)
    .map((h) => h.trim().replace(/^#/, ''))
    .filter(Boolean)
    .slice(0, 30)
}

const TASK_POLL_MS = 5000

function publishStatusLabel(status: string, t: (key: string, options?: { defaultValue?: string }) => string): string {
  const localized = t(`publishing:status.${status}`, { defaultValue: '' })
  if (localized) return localized
  return t(`status:${status}`, { defaultValue: status })
}

function agentStatusLabel(status: string, t: (key: string, options?: { defaultValue?: string }) => string): string {
  const map: Record<string, string> = {
    online: t('localPublisher:status.online'),
    offline: t('status:offline'),
    busy: t('localPublisher:status.busy'),
    auth_required: t('localPublisher:status.authRequired'),
    update_required: t('localPublisher:status.updateRequired'),
    revoked: t('localPublisher:status.revoked'),
  }
  return map[status] || t(`status:${status}`, { defaultValue: status })
}

function postConfirmBannerCopy(
  item: PublishItemDetail,
  t: (key: string) => string,
): { title: string; body: string } {
  if (item.status === 'scheduled') {
    return {
      title: t('publishing:banner.scheduledTitle'),
      body: t('publishing:banner.scheduledBody'),
    }
  }
  if (item.status === 'waiting_for_local_agent') {
    return {
      title: t('publishing:banner.waitingAgentTitle'),
      body: t('publishing:banner.waitingAgentBody'),
    }
  }
  if (item.status === 'awaiting_human_confirmation') {
    return {
      title: t('publishing:banner.humanConfirmTitle'),
      body: t('publishing:banner.humanConfirmBody'),
    }
  }
  if (item.status === 'publishing' || item.status === 'verifying_result') {
    return {
      title: t('publishing:banner.inProgressTitle'),
      body: t('publishing:banner.inProgressBody'),
    }
  }
  if (item.status === 'published') {
    return { title: t('publishing:banner.publishedTitle'), body: t('publishing:banner.publishedBody') }
  }
  return {
    title: t('publishing:banner.confirmedTitle'),
    body: t('publishing:banner.confirmedBody'),
  }
}

export default function ContentPublishingDetailPage() {
  const { t } = useTranslation(['publishing', 'actions', 'status', 'localPublisher'])
  const { locale } = useUiLocale()
  const { workspaceId = '', id: publicationId = '' } = useParams()
  const navigate = useNavigate()

  const [item, setItem] = useState<PublishItemDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const [titleDraft, setTitleDraft] = useState('')
  const [publishMode, setPublishMode] = useState<PublishMode>('immediate')
  const [scheduledAt, setScheduledAt] = useState<dayjs.Dayjs | null>(null)
  const [pkgDrafts, setPkgDrafts] = useState<Record<string, PkgDraft>>({})
  const [activePackageId, setActivePackageId] = useState<string | null>(null)

  const [capabilities, setCapabilities] = useState<PlatformCapabilityOut[]>([])
  const [accountsByPlatform, setAccountsByPlatform] = useState<Record<string, PlatformAccountOut[]>>({})
  const [addPlatformOpen, setAddPlatformOpen] = useState(false)

  // VISTOERA_PLATFORM_ACCOUNT_ONBOARDING_V1 — "添加平台账号" onboarding modal
  // state. Machine-agnostic: `onboardingDevices` is whatever the workspace's
  // real Publisher Devices currently are, never a hardcoded PC4 entry.
  const [onboardingPkgId, setOnboardingPkgId] = useState<string | null>(null)
  const [onboardingPlatform, setOnboardingPlatform] = useState<string | null>(null)
  const [onboardingPhase, setOnboardingPhase] = useState<'picking' | 'starting' | 'waiting' | 'connected' | 'failed'>('picking')
  const [onboardingDevices, setOnboardingDevices] = useState<LocalPublisherAgentSelectionOut[]>([])
  const [onboardingDeviceId, setOnboardingDeviceId] = useState<string | null>(null)
  const [onboardingAccount, setOnboardingAccount] = useState<PlatformAccountOut | null>(null)
  const onboardingPollRef = useRef<number | null>(null)

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmCheck, setConfirmCheck] = useState<PublishConfirmCheckResult | null>(null)
  const [confirming, setConfirming] = useState(false)

  const [localAgents, setLocalAgents] = useState<LocalPublisherAgentSelectionOut[]>([])
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null)
  const [jobProgress, setJobProgress] = useState<PublishJobProgressOut | null>(null)
  const [duplicating, setDuplicating] = useState(false)

  const load = useCallback(async () => {
    if (!workspaceId || !publicationId) return
    setLoading(true)
    setError(null)
    try {
      const { data } = await getPublishItemDetail(workspaceId, publicationId)
      setItem(data)
      setTitleDraft(data.title)
      setPublishMode(data.publish_mode)
      setScheduledAt(data.scheduled_at ? dayjs(data.scheduled_at) : null)
      setSelectedAgentId(data.selected_agent_id ?? null)
      const drafts: Record<string, PkgDraft> = {}
      data.packages.forEach((p) => {
        drafts[p.id] = toDraft(p)
      })
      setPkgDrafts(drafts)
      const selected = data.packages.filter((p) => p.selected_for_publish)
      setActivePackageId((prev) => (prev && selected.some((p) => p.id === prev) ? prev : selected[0]?.id ?? null))
    } catch (e) {
      setError((e as Error).message || t('publishing:toast.loadDetailFailed'))
    } finally {
      setLoading(false)
    }
  }, [workspaceId, publicationId, t])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (!workspaceId) return
    listPlatformCapabilities(workspaceId)
      .then(({ data }) => setCapabilities(data.items))
      .catch(() => setCapabilities([]))
  }, [workspaceId])

  const selectedPackages = useMemo(() => item?.packages.filter((p) => p.selected_for_publish) ?? [], [item])

  const needsLocalAgent = useMemo(
    () =>
      selectedPackages.some((p) => {
        const cap = capabilities.find((c) => c.platform_id === p.platform)
        return cap?.requires_local_agent
      }),
    [selectedPackages, capabilities],
  )

  const requiredPublisherCapabilities = useMemo(() => {
    const caps = new Set<string>()
    for (const pkg of selectedPackages) {
      const cap = capabilities.find((c) => c.platform_id === pkg.platform)
      if (cap?.publisher_capability) caps.add(cap.publisher_capability)
    }
    return [...caps]
  }, [selectedPackages, capabilities])

  useEffect(() => {
    if (!workspaceId || !needsLocalAgent) {
      setLocalAgents([])
      return
    }
    listLocalPublisherAgentsForSelection(workspaceId, requiredPublisherCapabilities)
      .then(({ data }) => setLocalAgents(data.items))
      .catch(() => setLocalAgents([]))
  }, [workspaceId, needsLocalAgent, requiredPublisherCapabilities.join('|')])

  const jobsClaimed = useMemo(() => (item?.jobs ?? []).some((j) => j.claimed), [item?.jobs])

  const canEditAgentSelection = useMemo(() => {
    if (!item) return false
    if (item.status === 'draft') return true
    if (item.status !== 'waiting_for_local_agent') return false
    const jobs = item.jobs ?? []
    if (jobs.length === 0) return true
    return jobs.every((j) => j.status === 'waiting_for_local_agent' && !j.claimed)
  }, [item])

  const canEditContent = item?.status === 'draft' || (item?.status === 'waiting_for_local_agent' && !jobsClaimed)

  const agentSelectOptions = useMemo(() => {
    const opts = localAgents.map((a) => ({
      value: a.id,
      disabled: !a.selectable,
      label: `${a.display_name}${a.machine_name ? ` — ${a.machine_name}` : ''}（${agentStatusLabel(a.status, t)}）`,
      capability: (a.capabilities ?? []).join(', '),
    }))
    const currentId = selectedAgentId ?? item?.selected_agent_id ?? null
    if (currentId && !opts.some((o) => o.value === currentId)) {
      const stale = item?.selected_agent
      opts.unshift({
        value: currentId,
        disabled: true,
        label: stale?.display_name
          ? `${stale.display_name}（${stale.unavailable_reason || (stale.status ? agentStatusLabel(stale.status, t) : t('publishing:agent.unavailable'))}）`
          : t('publishing:agent.originalUnavailable'),
        capability: '',
      })
    }
    return opts
  }, [localAgents, selectedAgentId, item?.selected_agent_id, item?.selected_agent, t])

  const singleLockedPlatform = useMemo(() => {
    const publishable = capabilities.filter((c) => c.publish_supported || c.requires_local_agent)
    if (publishable.length !== 1) return null
    const only = publishable[0]
    if (!selectedPackages.some((p) => p.platform === only.platform_id)) return null
    return only
  }, [capabilities, selectedPackages])

  const onSelectPublishAgent = async (agentId: string | null) => {
    if (!item || !canEditAgentSelection) return
    setSelectedAgentId(agentId)
    try {
      await updatePublishItem(workspaceId, item.id, { selected_agent_id: agentId })
      message.success(agentId ? t('publishing:toast.agentSelected') : t('publishing:toast.agentCleared'))
      await load()
    } catch (e) {
      message.error((e as Error).message || t('publishing:toast.agentSelectFailed'))
      await load()
    }
  }

  const activeJobId = item?.jobs?.[0]?.id ?? null

  useEffect(() => {
    if (!workspaceId || !activeJobId) return
    const interactive = ['waiting_for_local_agent', 'publishing', 'awaiting_human_confirmation', 'verifying_result']
    if (!item || !interactive.includes(item.status)) return
    const poll = () => {
      getPublishJobProgress(workspaceId, activeJobId)
        .then(({ data }) => setJobProgress(data))
        .catch(() => undefined)
    }
    poll()
    const t = window.setInterval(poll, TASK_POLL_MS)
    return () => window.clearInterval(t)
  }, [workspaceId, activeJobId, item?.status])

  const refreshAccounts = useCallback(
    (platform: string) => {
      listPlatformAccounts(workspaceId, platform)
        .then(({ data }) => setAccountsByPlatform((prev) => ({ ...prev, [platform]: data.items })))
        .catch(() => undefined)
    },
    [workspaceId],
  )

  useEffect(() => {
    if (!item) return
    const platforms = new Set(item.packages.map((p) => p.platform))
    platforms.forEach((p) => refreshAccounts(p))
  }, [item, refreshAccounts])

  const isEditable = item?.status === 'draft'
  const contentEditable = canEditContent
  const removedPackages = useMemo(() => item?.packages.filter((p) => !p.selected_for_publish) ?? [], [item])
  const activePackage = item?.packages.find((p) => p.id === activePackageId) ?? null
  const activeDraft = activePackageId ? pkgDrafts[activePackageId] : undefined

  const updateActiveDraft = (patch: Partial<PkgDraft>) => {
    if (!activePackageId) return
    setPkgDrafts((prev) => ({ ...prev, [activePackageId]: { ...prev[activePackageId], ...patch } }))
  }

  const saveDraft = async (): Promise<boolean> => {
    if (!item) return false
    setSaving(true)
    try {
      await updatePublishItem(workspaceId, item.id, {
        title: titleDraft.trim() || item.title,
        publish_mode: publishMode,
        scheduled_at: publishMode === 'scheduled' ? scheduledAt?.toISOString() ?? null : null,
        clear_scheduled_at: publishMode === 'immediate',
        selected_agent_id: selectedAgentId,
      })
      for (const pkg of item.packages) {
        const draft = pkgDrafts[pkg.id]
        if (draft && draftDirty(pkg, draft)) {
          await updatePublishPackage(workspaceId, item.id, pkg.id, {
            title: draft.title,
            caption: draft.caption,
            hashtags: parseHashtags(draft.hashtagsText),
          })
        }
      }
      message.success(t('publishing:toast.draftSaved'))
      await load()
      return true
    } catch (e) {
      message.error((e as Error).message || t('publishing:toast.saveFailed'))
      return false
    } finally {
      setSaving(false)
    }
  }

  const onAddPlatform = async (platformId: string) => {
    if (!item) return
    try {
      await addPublishPlatform(workspaceId, item.id, platformId)
      message.success(t('publishing:toast.platformAdded'))
      setAddPlatformOpen(false)
      await load()
    } catch (e) {
      message.error((e as Error).message || t('publishing:toast.addPlatformFailed'))
    }
  }

  const onRemovePlatform = async (pkg: PlatformPublishPackageOut) => {
    if (!item) return
    try {
      await removePublishPlatform(workspaceId, item.id, pkg.id)
      message.success(t('publishing:toast.platformRemoved'))
      await load()
    } catch (e) {
      message.error((e as Error).message || t('publishing:toast.removePlatformFailed'))
    }
  }

  const onSelectAccount = async (pkg: PlatformPublishPackageOut, accountId: string | null) => {
    if (!item) return
    try {
      await updatePublishPackage(workspaceId, item.id, pkg.id, {
        platform_account_id: accountId,
        clear_platform_account_id: accountId === null,
      })
      await load()
    } catch (e) {
      message.error((e as Error).message || t('publishing:toast.accountSelectFailed'))
    }
  }

  const stopOnboardingPoll = useCallback(() => {
    if (onboardingPollRef.current != null) {
      window.clearInterval(onboardingPollRef.current)
      onboardingPollRef.current = null
    }
  }, [])

  const closeOnboardingModal = useCallback(() => {
    stopOnboardingPoll()
    setOnboardingPkgId(null)
    setOnboardingPlatform(null)
    setOnboardingPhase('picking')
    setOnboardingDevices([])
    setOnboardingDeviceId(null)
    setOnboardingAccount(null)
  }, [stopOnboardingPoll])

  const openOnboardingModal = async (pkg: PlatformPublishPackageOut) => {
    setOnboardingPkgId(pkg.id)
    setOnboardingPlatform(pkg.platform)
    setOnboardingPhase('picking')
    setOnboardingDeviceId(null)
    setOnboardingAccount(null)
    try {
      const { data } = await listOnboardingDevices(workspaceId, pkg.platform)
      setOnboardingDevices(data.items)
      setOnboardingDeviceId(data.items.find((d) => d.selectable)?.id ?? null)
    } catch (e) {
      message.error((e as Error).message || t('publishing:toast.onboardingDevicesFailed'))
      setOnboardingDevices([])
    }
  }

  const pollOnboardingAccount = useCallback(
    (accountId: string) => {
      stopOnboardingPoll()
      const poll = () => {
        getPlatformAccount(workspaceId, accountId)
          .then(({ data }) => {
            setOnboardingAccount(data)
            if (data.connection_status === 'connected') {
              stopOnboardingPoll()
              setOnboardingPhase('connected')
              if (onboardingPkgId) {
                const pkg = item?.packages.find((p) => p.id === onboardingPkgId)
                if (pkg) void onSelectAccount(pkg, data.id)
              }
              if (onboardingPlatform) refreshAccounts(onboardingPlatform)
            } else if (data.connection_status === 'needs_reauth' || data.connection_status === 'disconnected') {
              stopOnboardingPoll()
              setOnboardingPhase('failed')
            }
          })
          .catch(() => undefined)
      }
      poll()
      onboardingPollRef.current = window.setInterval(poll, 3000)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workspaceId, onboardingPkgId, onboardingPlatform],
  )

  useEffect(() => () => stopOnboardingPoll(), [stopOnboardingPoll])

  useEffect(() => {
    if (onboardingPhase !== 'connected') return
    const t = window.setTimeout(() => closeOnboardingModal(), 1500)
    return () => window.clearTimeout(t)
  }, [onboardingPhase, closeOnboardingModal])

  const startOnboarding = async () => {
    if (!onboardingPlatform || !onboardingDeviceId) return
    setOnboardingPhase('starting')
    try {
      const { data } = await startPlatformAccountOnboarding(workspaceId, onboardingPlatform, onboardingDeviceId)
      setOnboardingAccount(data)
      setOnboardingPhase('waiting')
      pollOnboardingAccount(data.id)
    } catch (e) {
      message.error((e as Error).message || t('publishing:toast.onboardingStartFailed'))
      setOnboardingPhase('picking')
    }
  }

  const retryOnboarding = async () => {
    if (!onboardingAccount || !onboardingDeviceId) return
    setOnboardingPhase('starting')
    try {
      const { data } = await retryPlatformAccountOnboarding(workspaceId, onboardingAccount.id, onboardingDeviceId)
      setOnboardingAccount(data)
      setOnboardingPhase('waiting')
      pollOnboardingAccount(data.id)
    } catch (e) {
      message.error((e as Error).message || t('publishing:toast.onboardingRetryFailed'))
      setOnboardingPhase('failed')
    }
  }

  const onDuplicateAsDraft = async () => {
    if (!item) return
    setDuplicating(true)
    try {
      const { data } = await duplicatePublishItemAsDraft(workspaceId, item.id)
      message.success(t('publishing:toast.duplicateSuccess'))
      navigate(`/workspace/${workspaceId}/content-publishing/${data.id}`)
    } catch (e) {
      message.error((e as Error).message || t('publishing:toast.duplicateFailed'))
    } finally {
      setDuplicating(false)
    }
  }

  const openConfirmModal = async () => {
    if (!item) return
    const saved = await saveDraft()
    if (!saved) return
    try {
      const { data } = await checkPublishConfirmable(workspaceId, item.id)
      setConfirmCheck(data)
      setConfirmOpen(true)
    } catch (e) {
      message.error((e as Error).message || t('publishing:toast.confirmCheckFailed'))
    }
  }

  const doConfirm = async () => {
    if (!item) return
    setConfirming(true)
    try {
      const { data } = await confirmPublish(workspaceId, item.id)
      message.success(t('publishing:toast.confirmSuccess'))
      setConfirmOpen(false)
      setItem(data)
    } catch (e) {
      message.error((e as Error).message || t('publishing:toast.confirmFailed'))
    } finally {
      setConfirming(false)
    }
  }

  const renderAccountPicker = (pkg: PlatformPublishPackageOut) => {
    const accounts = accountsByPlatform[pkg.platform] ?? []
    if (accounts.length === 0) {
      return (
        <div className="cp-account-empty-state">
          <div className="cp-account-empty-title">{t('publishing:account.emptyTitle', { platform: pkg.platform_label })}</div>
          <div className="cp-account-empty-body">
            {t('publishing:account.emptyBody', { platform: pkg.platform_label })}
            <br />
            {t('publishing:account.noPassword', { platform: pkg.platform_label })}
          </div>
          <Button size="small" type="primary" disabled={!isEditable} onClick={() => openOnboardingModal(pkg)}>
            {t('publishing:account.addAccount', { platform: pkg.platform_label })}
          </Button>
        </div>
      )
    }
    return (
      <div className="cp-account-picker">
        <Select
          className="cp-platform-row-account"
          size="small"
          placeholder={t('publishing:account.selectAccount')}
          allowClear
          disabled={!isEditable}
          style={{ width: '100%' }}
          value={pkg.platform_account_id ?? undefined}
          optionLabelProp="label"
          onChange={(v) => onSelectAccount(pkg, v ?? null)}
          options={accounts.map((a) => ({
            value: a.id,
            label: `${a.platform_nickname || a.display_name} · ${a.connection_status_label}`,
            account: a,
          }))}
          optionRender={(option) => {
            const a = (option.data as { account?: PlatformAccountOut }).account
            if (!a) return option.label as string
            return (
              <div>
                <div>{a.platform_nickname || a.display_name}</div>
                <div className="cp-account-option-meta">
                  {a.platform_label} ·{' '}
                  <span className={`cp-account-status cp-account-status-${a.connection_status}`}>{a.connection_status_label}</span>
                </div>
              </div>
            )
          }}
        />
        <Button size="small" type="link" disabled={!isEditable} onClick={() => openOnboardingModal(pkg)}>
          {t('publishing:account.addNew')}
        </Button>
      </div>
    )
  }

  if (loading && !item) {
    return (
      <div className="cp-detail-page">
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    )
  }
  if (error || !item) {
    return <div className="cp-detail-page">{t('publishing:detail.loadFailed', { error })}</div>
  }

  const video = item.source_video
  const availablePlatformsToAdd = capabilities.filter((c) => !item.packages.some((p) => p.platform === c.platform_id))
  const reAddablePlatforms = removedPackages

  return (
    <div className="cp-detail-page">
      <div className="cp-detail-crumb">
        <Link to={`/workspace/${workspaceId}/content-publishing`}>
          <LeftOutlined /> {t('publishing:page.title')}
        </Link>
        <span> / {t('publishing:page.detailCrumb')}</span>
      </div>

      <div className="cp-detail-header">
        <div>
          {contentEditable ? (
            <Input
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              variant="borderless"
              style={{ fontSize: 20, fontWeight: 600, padding: 0 }}
            />
          ) : (
            <h2 className="tc-page-title">{item.title}</h2>
          )}
          <div className="cp-detail-header-meta">
            <span>{t('publishing:detail.video')}</span>
            {video.duration_seconds != null && <span>{t('publishing:detail.seconds', { count: Math.round(video.duration_seconds) })}</span>}
            <span>{t('publishing:card.createdAt', { time: item.created_at ? formatDateTime(item.created_at, locale) : '—' })}</span>
            <span className="tc-badge tc-badge-info">{publishStatusLabel(item.status, t)}</span>
          </div>
        </div>
        <div className="tc-page-actions">
          {contentEditable && (
            <Button onClick={saveDraft} loading={saving}>
              {isEditable ? t('publishing:detail.saveDraft') : t('publishing:detail.saveChanges')}
            </Button>
          )}
          <Button type="primary" onClick={openConfirmModal} disabled={!isEditable || selectedPackages.length === 0}>
            {t('publishing:detail.confirmPublish')}
          </Button>
        </div>
      </div>

      {!isEditable && (
        <div className="cp-confirm-banner">
          <div className="cp-confirm-banner-title">{postConfirmBannerCopy(item, t).title}</div>
          <div>{postConfirmBannerCopy(item, t).body}</div>
          {item.status === 'waiting_for_local_agent' && jobsClaimed && (
            <div className="cp-contaminated-job-panel">
              <div>{t('publishing:banner.claimedPanel')}</div>
              <Button type="primary" loading={duplicating} onClick={onDuplicateAsDraft} style={{ marginTop: 8 }}>
                {t('publishing:banner.createNewTask')}
              </Button>
            </div>
          )}
          {item.scheduled_at && (
            <div className="cp-confirm-jobs">
              {t('publishing:schedule.plannedAt', { time: formatDateTime(item.scheduled_at, locale) })}
            </div>
          )}
          {jobProgress?.requires_user_action && (
            <div className="cp-human-confirm-panel">
              <div className="cp-human-confirm-title">{t('publishing:banner.humanConfirmPanelTitle')}</div>
              <Button
                type="primary"
                onClick={() => activeJobId && focusPublishWindow(workspaceId, activeJobId).catch(() => undefined)}
              >
                {t('publishing:banner.openPublishWindow')}
              </Button>
            </div>
          )}
          {jobProgress?.remote_post_id && (
            <div className="cp-confirm-jobs">{t('publishing:banner.remotePostId', { id: jobProgress.remote_post_id })}</div>
          )}
        </div>
      )}

      <div className="cp-detail-main">
        <div className="cp-detail-preview-col">
          <div className="tc-data-card">
            <div className="cp-video-frame">
              {video.available ? (
                <video src={videoGenTaskOutputUrl(workspaceId, item.source_asset_id)} controls poster={videoGenTaskPosterUrl(workspaceId, item.source_asset_id)} />
              ) : (
                <div className="cp-video-frame-empty">
                  <VideoCameraOutlined style={{ fontSize: 28 }} />
                  <span>{t('publishing:detail.videoUnavailable')}</span>
                </div>
              )}
            </div>
            <div className="cp-video-meta-row">
              <span>{video.content_type || 'MP4'}</span>
              {video.width && video.height && <span>{video.width} × {video.height}</span>}
              {video.duration_seconds != null && (
                <span>
                  {String(Math.floor(video.duration_seconds / 60)).padStart(2, '0')}:
                  {String(Math.round(video.duration_seconds % 60)).padStart(2, '0')}
                </span>
              )}
              {video.aspect_ratio && <span>{video.aspect_ratio}</span>}
            </div>
            <div className="cp-cover-row">
              <div className="cp-cover-thumb">
                <img src={videoGenTaskPosterUrl(workspaceId, item.source_asset_id)} alt="" />
              </div>
              <div>
                <div className="cp-field-label" style={{ marginBottom: 2 }}>
                  {t('publishing:detail.cover')}
                </div>
                <Tag>{t('publishing:detail.coverComingSoon')}</Tag>
              </div>
            </div>

            <div className="cp-platform-tabs-row">
              {selectedPackages.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={`cp-platform-tab-chip${p.id === activePackageId ? ' is-active' : ''}`}
                  onClick={() => setActivePackageId(p.id)}
                >
                  {p.platform_label}
                  {!p.is_complete && <span title={t('publishing:detail.missingTitle')}>!</span>}
                </button>
              ))}
              {isEditable && (
                <button type="button" className="cp-add-platform-chip" onClick={() => setAddPlatformOpen(true)}>
                  <PlusOutlined /> {t('publishing:detail.addPlatform')}
                </button>
              )}
            </div>

            {activePackage && activeDraft ? (
              <div className="cp-platform-editor">
                <div>
                  <span className="cp-field-label">{t('publishing:detail.title')}</span>
                  <Input
                    value={activeDraft.title}
                    disabled={!contentEditable}
                    onChange={(e) => updateActiveDraft({ title: e.target.value })}
                    maxLength={300}
                  />
                </div>
                <div>
                  <span className="cp-field-label">{t('publishing:detail.caption')}</span>
                  <Input.TextArea
                    value={activeDraft.caption}
                    disabled={!contentEditable}
                    onChange={(e) => updateActiveDraft({ caption: e.target.value })}
                    autoSize={{ minRows: 3, maxRows: 6 }}
                  />
                </div>
                <div>
                  <span className="cp-field-label">{t('publishing:detail.hashtags')}</span>
                  <Input
                    value={activeDraft.hashtagsText}
                    disabled={!contentEditable}
                    onChange={(e) => updateActiveDraft({ hashtagsText: e.target.value })}
                    placeholder={t('publishing:detail.hashtagsPlaceholder')}
                  />
                </div>
              </div>
            ) : (
              <div className="cp-platform-editor">
                <span style={{ color: 'var(--text-muted)' }}>{t('publishing:detail.selectPlatformHint')}</span>
              </div>
            )}
          </div>
        </div>

        <div className="cp-detail-settings-col">
          <div className="tc-data-card cp-settings-panel" style={{ padding: 16 }}>
            <div>
              <div className="cp-settings-section-title">{t('publishing:settings.platforms')}</div>
              {singleLockedPlatform ? (
                <>
                  <div className="cp-single-platform-locked">
                    <span className="cp-platform-row-icon">{singleLockedPlatform.display_name.slice(0, 1)}</span>
                    <span className="cp-platform-row-name">{singleLockedPlatform.display_name}</span>
                    <Tag color="blue">{t('publishing:platform.browserAssisted')}</Tag>
                  </div>
                  {selectedPackages[0] ? (
                    <div className="cp-platform-row" style={{ marginTop: 8 }}>
                      {renderAccountPicker(selectedPackages[0])}
                    </div>
                  ) : null}
                </>
              ) : (
              <div className="cp-platforms-list">
                {selectedPackages.map((p) => (
                  <div key={p.id} className="cp-platform-row">
                    <span className="cp-platform-row-icon">{p.platform_label.slice(0, 1)}</span>
                    <span className="cp-platform-row-name">{p.platform_label}</span>
                    {renderAccountPicker(p)}
                    {isEditable && (
                      <Button size="small" type="text" icon={<CloseOutlined />} onClick={() => onRemovePlatform(p)} />
                    )}
                  </div>
                ))}
                {isEditable && (
                  <Button className="cp-add-platform-btn" icon={<PlusOutlined />} onClick={() => setAddPlatformOpen(true)}>
                    {t('publishing:detail.addPlatform')}
                  </Button>
                )}
              </div>
              )}
            </div>

            {needsLocalAgent && (
              <div className="cp-device-select-panel">
                <div className="cp-settings-section-title">{t('publishing:settings.publishMode')}</div>
                <Tag color="blue">{t('publishing:platform.browserAssisted')}</Tag>
                <div className="cp-settings-section-title" style={{ marginTop: 12 }}>
                  {t('publishing:settings.localPublisher')}
                </div>
                {localAgents.length === 0 ? (
                  <div className="cp-agent-empty">
                    <div>{t('publishing:agent.noDevices')}</div>
                    <Link to={`/workspace/${workspaceId}/local-publishers`}>{t('publishing:agent.manageDevices')}</Link>
                  </div>
                ) : (
                  <>
                    <Select
                      className="cp-device-select"
                      placeholder={t('publishing:agent.selectDevice')}
                      disabled={!canEditAgentSelection}
                      value={selectedAgentId ?? undefined}
                      optionLabelProp="label"
                      onChange={(v) => onSelectPublishAgent(v ?? null)}
                      options={agentSelectOptions}
                      optionRender={(option) => {
                        const agent = localAgents.find((a) => a.id === option.value)
                        const reason = agent?.disabled_reason
                        const capability = (option.data as { capability?: string })?.capability
                        return (
                          <div className={reason ? 'cp-device-option-disabled' : undefined}>
                            <div>{option.label}</div>
                            {capability ? <div className="cp-device-option-cap">{capability}</div> : null}
                            {reason && agent && !agent.selectable ? (
                              <div className="cp-device-option-reason">{reason}</div>
                            ) : null}
                          </div>
                        )
                      }}
                    />
                    {!canEditAgentSelection && item?.status === 'waiting_for_local_agent' ? (
                      <div className="cp-schedule-hint">{t('publishing:agent.claimedHint')}</div>
                    ) : null}
                    <Link className="cp-device-manage-link" to={`/workspace/${workspaceId}/local-publishers`}>
                      {t('publishing:agent.manageDevices')}
                    </Link>
                  </>
                )}
                <div className="cp-schedule-hint">{t('publishing:agent.humanConfirmHint')}</div>
              </div>
            )}

            <div>
              <div className="cp-settings-section-title">{t('publishing:schedule.title')}</div>
              <div className="cp-schedule-panel">
                <Radio.Group
                  disabled={!isEditable}
                  value={publishMode}
                  onChange={(e) => setPublishMode(e.target.value)}
                >
                  <Radio value="immediate">{t('publishing:schedule.immediate')}</Radio>
                  <Radio value="scheduled">{t('publishing:schedule.scheduled')}</Radio>
                </Radio.Group>
                {publishMode === 'scheduled' && (
                  <DatePicker
                    showTime
                    disabled={!isEditable}
                    value={scheduledAt}
                    onChange={setScheduledAt}
                    style={{ width: '100%' }}
                    disabledDate={(d) => d && d.isBefore(dayjs().startOf('day'))}
                  />
                )}
              </div>
            </div>

            {contentEditable && !isEditable && (
              <Button type="primary" block onClick={saveDraft} loading={saving}>
                {t('publishing:detail.saveChanges')}
              </Button>
            )}
            {isEditable && (
              <Button type="primary" block onClick={saveDraft} loading={saving}>
                {t('publishing:detail.saveDraft')}
              </Button>
            )}
          </div>
        </div>
      </div>

      <Modal
        open={addPlatformOpen}
        onCancel={() => setAddPlatformOpen(false)}
        title={t('publishing:platform.addModalTitle')}
        footer={null}
        destroyOnClose
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {reAddablePlatforms.length > 0 && (
            <>
              <div className="cp-field-label">{t('publishing:platform.reAddSection')}</div>
              {reAddablePlatforms.map((p) => (
                <PlatformPickRow
                  key={p.id}
                  label={p.platform_label}
                  status={t('publishing:platform.configurable')}
                  onClick={() => onAddPlatform(p.platform)}
                />
              ))}
            </>
          )}
          <div className="cp-field-label">{t('publishing:platform.allPlatforms')}</div>
          {availablePlatformsToAdd.map((c) => (
            <PlatformPickRow
              key={c.platform_id}
              label={c.display_name}
              status={
                c.publisher_mode === 'browser_assisted'
                  ? t('publishing:platform.browserAssisted')
                  : c.publish_supported
                    ? t('publishing:platform.configurable')
                    : t('publishing:platform.comingSoon')
              }
              onClick={() => onAddPlatform(c.platform_id)}
            />
          ))}
        </div>
      </Modal>

      <Modal
        open={!!onboardingPkgId}
        onCancel={closeOnboardingModal}
        title={t('publishing:account.onboardingModalTitle', {
          platform: capabilities.find((c) => c.platform_id === onboardingPlatform)?.display_name || onboardingPlatform || '',
        })}
        footer={null}
        destroyOnClose
      >
        <div className="cp-onboarding-modal">
          {onboardingPhase === 'picking' && (
            <>
              <div className="cp-onboarding-hint">
                {t('publishing:account.onboardingHint', {
                  platform: capabilities.find((c) => c.platform_id === onboardingPlatform)?.display_name || t('publishing:platform.allPlatforms'),
                })}
                <br />
                {t('publishing:account.onboardingPrivacy')}
              </div>
              <div className="cp-field-label" style={{ marginTop: 12 }}>
                {t('publishing:account.selectDevice')}
              </div>
              {onboardingDevices.length === 0 ? (
                <div className="cp-agent-empty">
                  <div>{t('publishing:account.noOnlineAgent')}</div>
                  <Link to={`/workspace/${workspaceId}/local-publishers`}>{t('publishing:agent.manageDevices')}</Link>
                </div>
              ) : (
                <Select
                  style={{ width: '100%', marginTop: 6 }}
                  value={onboardingDeviceId ?? undefined}
                  onChange={(v) => setOnboardingDeviceId(v)}
                  options={onboardingDevices.map((d) => ({
                    value: d.id,
                    disabled: !d.selectable,
                    label: `${d.display_name}${d.machine_name ? ` — ${d.machine_name}` : ''}（${agentStatusLabel(d.status, t)}）`,
                  }))}
                />
              )}
              <Button
                type="primary"
                block
                style={{ marginTop: 16 }}
                disabled={!onboardingDeviceId}
                onClick={startOnboarding}
              >
                {t('publishing:account.startAdd')}
              </Button>
            </>
          )}
          {onboardingPhase === 'starting' && (
            <div className="cp-onboarding-waiting">
              <Spin /> <span>{t('publishing:account.creatingTask')}</span>
            </div>
          )}
          {onboardingPhase === 'waiting' && (
            <div className="cp-onboarding-waiting">
              <Spin />
              <div>
                <div>
                  {t('publishing:account.completeLoginOn', {
                    device: onboardingDevices.find((d) => d.id === onboardingDeviceId)?.display_name || 'Publisher Device',
                  })}
                </div>
                <div className="cp-onboarding-hint">{t('publishing:account.loginHint')}</div>
              </div>
            </div>
          )}
          {onboardingPhase === 'connected' && (
            <div className="cp-onboarding-success">
              <CheckCircleFilled style={{ color: 'var(--success-text)', fontSize: 20 }} />
              <div>
                {t('publishing:account.connected', {
                  name: onboardingAccount?.platform_nickname || onboardingAccount?.display_name || '',
                })}
              </div>
            </div>
          )}
          {onboardingPhase === 'failed' && (
            <div className="cp-onboarding-failed">
              <div>{onboardingAccount?.onboarding_task?.error_message || t('publishing:account.loginIncomplete')}</div>
              <Button icon={<ReloadOutlined />} onClick={retryOnboarding} disabled={!onboardingDeviceId} style={{ marginTop: 8 }}>
                {t('actions:retry')}
              </Button>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        title={t('publishing:confirm.title')}
        okText={t('publishing:confirm.ok')}
        cancelText={t('publishing:confirm.cancel')}
        onOk={doConfirm}
        confirmLoading={confirming}
        okButtonProps={{ disabled: !confirmCheck?.can_confirm }}
      >
        {confirmCheck && (
          <>
            <p>
              {t('publishing:confirm.contentTo', {
                title: item.title,
                platforms: selectedPackages.map((p) => p.platform_label).join('、') || t('publishing:confirm.noPlatform'),
              })}
            </p>
            <p>
              {t('publishing:confirm.publishTime', {
                time:
                  publishMode === 'scheduled'
                    ? scheduledAt
                      ? formatDateTime(scheduledAt.toISOString(), locale)
                      : t('publishing:confirm.notSet')
                    : t('publishing:confirm.immediate'),
              })}
            </p>
            <div className="cp-confirm-check-list">
              {Object.entries(confirmCheck.checks).map(([key, pass]) => (
                <div key={key} className={`cp-confirm-check-item ${pass ? 'is-pass' : 'is-fail'}`}>
                  <CheckCircleFilled /> {t(`publishing:confirm.checks.${key}`, { defaultValue: key })}：
                  {pass ? t('publishing:confirm.pass') : t('publishing:confirm.fail')}
                </div>
              ))}
            </div>
          </>
        )}
      </Modal>
    </div>
  )
}

function PlatformPickRow({ label, status, onClick }: { label: string; status: string; onClick: () => void }) {
  return (
    <button type="button" className="cp-platform-row" style={{ cursor: 'pointer', width: '100%', textAlign: 'left', background: 'none' }} onClick={onClick}>
      <span className="cp-platform-row-icon">{label.slice(0, 1)}</span>
      <span style={{ flex: 1 }}>{label}</span>
      <Tag>{status}</Tag>
    </button>
  )
}
